import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { Value } from './value';

export interface Column<T> {
  key: string;
  header?: ReactNode;
  render?(row: T): ReactNode;
  className?: string;
  sortable?: boolean;
  align?: 'start' | 'end' | 'center';
}

const HIDDEN = new Set(['id', 'tenantId', 'updatedAt', 'deletedAt', 'version', 'metadata', 'payload', 'content', 'blocks', 'seo', 'routeGeojson', 'audience', 'rules', 'openingHours', 'qrToken', 'creditConsumptionIds', 'remindersSent', 'contentHash', 'pdfSha256', 'providerPaymentId', 'checkoutUrl', 'bodyEn', 'bodyAr', 'descriptionAr', 'nameAr', 'titleAr', 'labelAr', 'answerAr', 'questionAr', 'excerptAr', 'messageAr', 'headlineAr', 'subtitleAr', 'captionAr', 'ctaAr', 'publicDescriptionAr']);
const PRIORITY = ['name', 'nameEn', 'titleEn', 'title', 'firstName', 'reference', 'number', 'code', 'label', 'labelEn', 'key', 'subject', 'questionEn', 'kind', 'type', 'status', 'personId', 'holderPersonId', 'horseId', 'serviceId', 'planId', 'startsAt', 'date', 'dueAt', 'priceAmount', 'amount', 'total', 'monthlyAmount', 'stockQty', 'active'];

/** Pick sensible columns from the data itself when a page does not declare them. */
export function inferColumns<T extends Record<string, unknown>>(sample: T[], max = 7): Array<Column<T>> {
  const keys = new Set<string>();
  for (const r of sample.slice(0, 10)) Object.keys(r).forEach((k) => keys.add(k));
  const usable = [...keys].filter((k) => !HIDDEN.has(k) && !sample.every((r) => r[k] === null || r[k] === undefined || (Array.isArray(r[k]) && !(r[k] as unknown[]).length)));
  const ranked = usable.sort((a, b) => {
    const ia = PRIORITY.indexOf(a);
    const ib = PRIORITY.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  });
  const cols = ranked.filter((k) => k !== 'lastName').slice(0, max);
  if (keys.has('createdAt') && cols.length < max && !cols.includes('createdAt')) cols.push('createdAt');
  return cols.map((key) => (key === 'firstName' && keys.has('lastName') ? { key, header: 'Name', render: (r: T) => `${r.firstName ?? ''} ${r.lastName ?? ''}` } : { key }));
}

export function DataTable<T extends Record<string, unknown>>({
  rows,
  columns,
  loading,
  onRowClick,
  empty,
  context = '',
  rowKey = (r) => String(r.id ?? JSON.stringify(r)),
  footer,
  dense,
  selected,
}: {
  rows: T[];
  columns?: Array<Column<T>>;
  loading?: boolean;
  onRowClick?(row: T): void;
  empty?: ReactNode;
  context?: string;
  rowKey?(row: T): string;
  footer?: ReactNode;
  dense?: boolean;
  selected?: string | null;
}) {
  const { label } = useI18n();
  const cols = useMemo(() => columns ?? inferColumns(rows), [columns, rows]);
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);
  const sorted = useMemo(() => {
    if (!sort) return rows;
    return [...rows].sort((a, b) => {
      const x = a[sort.key] as never;
      const y = b[sort.key] as never;
      if (x === y) return 0;
      if (x === null || x === undefined) return 1;
      if (y === null || y === undefined) return -1;
      return (x > y ? 1 : -1) * sort.dir;
    });
  }, [rows, sort]);

  return (
    <div className="card-surface overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b bg-muted/40">
              {cols.map((c) => (
                <th key={c.key} className={cn('whitespace-nowrap px-4 py-2.5 text-start text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground', c.align === 'end' && 'text-end', c.className)}>
                  {c.sortable !== false ? (
                    <button className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => setSort((s) => (s?.key === c.key ? (s.dir === 1 ? { key: c.key, dir: -1 } : null) : { key: c.key, dir: 1 }))}>
                      {c.header ?? label(c.key)}
                      {sort?.key === c.key ? sort.dir === 1 ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" /> : <ChevronsUpDown className="size-3 opacity-40" />}
                    </button>
                  ) : (
                    c.header ?? label(c.key)
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading &&
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="border-b last:border-0">
                  {cols.map((c) => (
                    <td key={c.key} className="px-4 py-3">
                      <Skeleton className="h-4 w-[70%]" />
                    </td>
                  ))}
                </tr>
              ))}
            {!loading &&
              sorted.map((row) => {
                const key = rowKey(row);
                return (
                  <tr
                    key={key}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={cn('border-b transition-colors last:border-0', onRowClick && 'cursor-pointer hover:bg-muted/50', selected === key && 'bg-primary/5')}
                  >
                    {cols.map((c) => (
                      <td key={c.key} className={cn('max-w-[22rem] px-4 align-middle', dense ? 'py-2' : 'py-3', c.align === 'end' && 'text-end', c.className)}>
                        {c.render ? c.render(row) : <Value k={c.key} v={row[c.key]} context={context} row={row} />}
                      </td>
                    ))}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      {!loading && rows.length === 0 && (empty ?? <EmptyState title="Nothing here yet" description="Records you create will appear here." />)}
      {footer}
    </div>
  );
}

export function LoadMore({ hasMore, loading, onClick }: { hasMore: boolean; loading: boolean; onClick(): void }) {
  const { t } = useI18n();
  if (!hasMore) return null;
  return (
    <div className="flex justify-center border-t p-3">
      <Button variant="ghost" size="sm" loading={loading} onClick={onClick}>
        {t('Load more')}
      </Button>
    </div>
  );
}
